import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Button, Card, Descriptions, Result, Skeleton, Space, Typography } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { ApiService } from "../../api";
import type {
  ApprovalLink,
  ApprovalLinkReason,
  ApprovalLinkResult,
} from "../../api";

/**
 * The page an "Approve" button in a notification email opens.
 *
 * Signed out by design: the token in the URL is the credential (see
 * backend/main/approval_links.py). Two consequences shape this screen.
 *
 * **It confirms, it does not act on arrival.** Loading it only fetches a
 * summary. Approving is an explicit click, because mail security products
 * fetch every URL in every message unattended — if opening the page approved,
 * the scanner would approve the permit before the approver read the mail.
 *
 * **It shows the least it can.** Whoever holds the link is not authenticated
 * and may not even be the intended approver (a forwarded thread), so this
 * shows which approval is pending and nothing about the permit's contents.
 * Everything else stays behind the login.
 */

const CARD_WIDTH = 460;

const EmailApprovePage: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useParams();

  const [link, setLink] = useState<ApprovalLink | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<ApprovalLinkResult | null>(null);
  const [failed, setFailed] = useState(false);

  // The server's reasons, in the user's language. The server sends English
  // copy too, used only if it ever grows a reason this build does not know.
  const reasonText = useCallback(
    (reason: ApprovalLinkReason, fallback: string) => {
      const copy: Partial<Record<ApprovalLinkReason, string>> = {
        invalid: t("This approval link is not valid."),
        expired: t("This approval link has expired. Open the request to approve it."),
        used: t("This approval link has already been used."),
        superseded: t(
          "This request has moved on since the email was sent. Open the request to see where it stands."
        ),
        decided: t("This approval step has already been decided."),
        not_eligible: t("This step must be approved in the application."),
      };
      return copy[reason] || fallback;
    },
    [t]
  );

  useEffect(() => {
    let cancelled = false;
    ApiService.apiMainApprovalLinkRetrieve(token!)
      .then((result) => {
        if (!cancelled) setLink(result);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const approve = async () => {
    setSubmitting(true);
    try {
      const result = await ApiService.apiMainApprovalLinkCreate(token!);
      // A refusal can arrive here rather than at load: somebody may have
      // approved in the app, or returned the permit, while this page was open.
      if ("valid" in result && result.valid === false) {
        setLink(result);
      } else {
        setDone(result);
      }
    } catch {
      setFailed(true);
    } finally {
      setSubmitting(false);
    }
  };

  // One white surface for whatever state the page is in, so the skeleton, the
  // question, the refusal and the receipt all occupy the same box instead of
  // the layout jumping as each replaces the last.
  const surface = (children: React.ReactNode) => (
    <Card style={{ width: CARD_WIDTH }} bodyStyle={{ padding: 28 }}>
      {children}
    </Card>
  );

  if (loading) {
    return surface(<Skeleton active paragraph={{ rows: 5 }} />);
  }

  if (failed || !link) {
    return surface(
      <Result
        style={{ padding: 0 }}
        status="warning"
        title={t("Something went wrong.")}
        subTitle={t("Please open the request in the application to approve it.")}
      />
    );
  }

  if (done && "approved" in done) {
    return surface(
      <Result
        style={{ padding: 0 }}
        status="success"
        title={t("Approved")}
        subTitle={
          done.fully_approved
            ? t("Request %{number} has cleared every approval step.").replace(
                "%{number}",
                done.request_number
              )
            : t("Your approval of '%{step}' on request %{number} is recorded.")
                .replace("%{step}", done.workflow)
                .replace("%{number}", done.request_number)
        }
        extra={
          <Button href={done.open_url}>{t("Open request")}</Button>
        }
      />
    );
  }

  if (link.valid === false) {
    return surface(
      <Result
        style={{ padding: 0 }}
        status="info"
        title={t("This link can no longer be used")}
        subTitle={reasonText(link.reason, link.message)}
        extra={
          <Button type="primary" href="/">
            {t("Open the application")}
          </Button>
        }
      />
    );
  }

  const { request, workflow, role, approver } = link;

  return surface(
    <Space direction="vertical" size={20} style={{ width: "100%" }}>
      <div>
        <Typography.Title level={4} style={{ marginTop: 0, marginBottom: 4 }}>
          {t("Approve this request?")}
        </Typography.Title>
        <Typography.Text type="secondary">
          {t("Signing as %{name}").replace("%{name}", approver.name)}
          {approver.email ? ` · ${approver.email}` : ""}
        </Typography.Text>
      </div>

      {/* Not `bordered`: the card is already the boundary, and a second frame
          inside it is a box in a box. */}
      <Descriptions
        size="small"
        column={1}
        colon={false}
        labelStyle={{ width: 130, color: "rgba(0,0,0,.45)" }}
        items={[
          { key: "number", label: t("Request No."), children: request.number },
          ...(request.title
            ? [{ key: "title", label: t("Title"), children: request.title }]
            : []),
          { key: "step", label: t("Approval step"), children: workflow.name },
          ...(role ? [{ key: "role", label: t("Role"), children: role }] : []),
          { key: "status", label: t("Status"), children: request.status },
        ]}
      />

      {/* Said plainly, because this button records a signature on a permit and
          the person clicking it has not opened the request. */}
      <Alert
        type="info"
        showIcon
        message={t(
          "Approving here records your approval immediately. Open the request first if you want to review the details."
        )}
      />

      <Space>
        <Button
          type="primary"
          size="large"
          icon={<CheckCircleOutlined />}
          loading={submitting}
          onClick={approve}
        >
          {t("Approve")}
        </Button>
        <Button size="large" href={link.open_url}>
          {t("Review in app")}
        </Button>
      </Space>
    </Space>
  );
};

export default EmailApprovePage;
